<template>
  <q-page class="q-pa-sm">
    <breadcrumbs />
    <div>
      <q-input
        v-model="searchTerms"
        class="q-mt-sm"
        dense
        label="Search operations..."
        input-class="allow-global-search"
        autofocus
      />
      <transition-group name="list-complete">
        <menu-item-category
          v-for="category in filteredList"
          :key="category.label"
          :label="category.label"
        >
          <menu-item-card
            v-for="item in category.entries"
            :key="item.label"
            :item="item"
            default-icon="insert_chart_outlined"
            :notification-count="notificationCount(item)"
            @click="selectItem(item)"
          />
        </menu-item-category>
      </transition-group>
    </div>
    <tms-export-dialog ref="tmsExportDialog" />
    <polaris-export-dialog ref="polarisExportDialog" />
  </q-page>
</template>

<script>
import { ADJUSTMENT_TYPE_OPTIONS, ADJUSTMENT_TYPES_PLURAL } from 'src/models/inventory-item/InventoryItemAdjustment'
import Model from 'src/models/model'
import Breadcrumbs from 'src/modules/admin/components/Breadcrumbs'
import AdminMenuMixin from 'src/modules/admin/menu/AdminMenuMixin'
import MenuItemCard from 'src/modules/admin/menu/MenuItemCard'
import MenuItemCategory from 'src/modules/admin/menu/MenuItemCategory'
import PolarisExportDialog from 'src/modules/admin/polaris/PolarisExportDialog'
import TmsExportDialog from 'src/modules/admin/tms/TmsExportDialog'

const NOTIFICATION_COUNT_TYPES = {
  PENDING_INVENTORY_ITEM_ADJUSTMENTS: 'pending_inventory_item_adjustments'
}

/**
 * @typedef {import("./AdminMenuMixin").MenuItem} MenuItem
 * @type {MenuItem[]}
 */
export const operationsMenuItems = [{
  label: 'Customers',
  entries: [
    {
      indent: 1,
      route: 'users',
      label: 'Users',
      icon: 'person',
      description: 'View users of the site. Edit users, find past orders, modify memberships, and more.',
      requiresPermission: Model.permissions.Customer.View
    },
    {
      indent: 1,
      route: 'orders',
      label: 'Orders',
      icon: 'toc',
      description: 'Manage orders. Search by user, employee, register, payment details. View, print or email receipts.',
      requiresPermission: Model.permissions.Customer.View
    },
    {
      indent: 1,
      route: 'payments',
      label: 'Payments',
      icon: 'paid',
      description: 'View recorded payments. Lookup associated users or orders from payment data.',
      requiresPermission: Model.permissions.Customer.View
    },
    {
      indent: 1,
      route: 'memberships',
      label: 'Memberships',
      icon: 'card_membership',
      description: 'View and manage active and past memberships. View histories and update renewals.',
      requiresPermission: Model.permissions.Customer.ViewMemberships
    },
    {
      indent: 1,
      route: 'vouchers',
      label: 'Vouchers',
      icon: 'sell',
      description: 'View and manage active and used vouchers. Assign users and physical cards, or update expiries.',
      requiresPermission: Model.permissions.Customer.View
    },
    {
      indent: 1,
      route: 'gift-cards',
      label: 'Gift Cards',
      icon: 'redeem',
      description: 'View and manage active and used gift cards. Assign users and physical cards, or update expiries.',
      requiresPermission: Model.permissions.Customer.View
    },
    {
      indent: 1,
      route: 'cards',
      label: 'Cards',
      icon: 'credit_card',
      description: 'View and manage active cards assigned to users, vouchers, or gift cards.',
      requiresPermission: Model.permissions.Customer.View
    },
    {
      indent: 1,
      route: 'visit-reviews',
      label: 'Visit Reviews',
      icon: 'reviews',
      description: 'View feedback provided by your customers.',
      requiresPermission: Model.permissions.Marketing.View
    }
  ]
},
{
  label: 'Programming',
  library: true,
  requiresPermission: Model.permissions.FilmProgramming.View,
  entries: [
    {
      indent: 1,
      route: 'booking-weeks',
      label: 'Booking Weeks',
      description: 'Create showing plans and schedule movies for coming weeks.',
      icon: 'calendar_view_week'
    },
    {
      route: 'movies',
      label: 'Movies',
      description: 'Create and manage movies, including posters, ratings, distribution terms and more. Shown on your Website, Register, and Signage.',
      icon: 'local_movies',
      library: true
    },
    {
      route: 'showings',
      label: 'Showings',
      description: 'View all scheduled and planned showings. Lookup associated tickets and orders.',
      icon: 'preview'
    }
  ]
},
{
  label: 'Studio Reporting',
  library: true,
  requiresAnyPermission: [Model.permissions.StudioReporting.View, Model.permissions.FilmSettlement.View],
  entries: [
    {
      indent: 1,
      route: 'box-office-reports',
      label: 'Box Office Reports',
      description: 'View, search and send Box Office Reports.',
      icon: 'table_view'
    },
    {
      indent: 1,
      route: 'box-office-report-settlements',
      label: 'Box Office Report Settlements',
      description: 'View and manage Box Office Report Settlements for rental fees owed on your Box Office Reports.',
      icon: 'table_view',
      requiresPermission: Model.permissions.FilmSettlement.View,
      library: true
    },
    {
      indent: 1,
      route: 'studio-settlements',
      label: 'Studio Settlements',
      description: 'View and manage Studio Settlements.',
      icon: 'calendar_view_week',
      requiresPermission: Model.permissions.FilmSettlement.View,
      library: true
    },
    {
      indent: 1,
      route: 'studios',
      label: 'Studios',
      description: 'Create and manage distribution studios used for generating Box Office Reports.',
      icons: 'movie',
      library: true
    },
    {
      route: 'movie-terms',
      label: 'Movie Terms',
      description: 'Create and manage movie terms for your movies, used to set and adjust rental fees on your Box Office Reports.',
      icon: 'movie_edit',
      library: true
    }
  ]
},
{
  label: 'Website and Marketing',
  description: '',
  library: true,
  entries: [
    {
      indent: 1,
      label: 'News Items',
      icon: 'post_add',
      description: 'Create and manage promotional content for your Website and App.',
      route: 'news-items',
      requiresPermission: Model.permissions.SiteContent.View
    },
    {
      indent: 1,
      label: 'Broadcasts',
      icon: 'campaign',
      description: 'Create and send CustomerIO broadcast triggers.',
      route: 'broadcasts',
      customerio: true,
      circuit: true,
      requiresPermission: Model.permissions.Marketing.View
    },
    {
      indent: 1,
      label: 'Form Entries',
      icon: 'list',
      description: 'View Web Form Submissions.',
      route: 'form-entries',
      site: true,
      requiresPermission: Model.permissions.Marketing.View
    },
    {
      indent: 1,
      label: 'CustomerIO Segment Promotions',
      icon: 'list',
      description: 'Create and manage deals and promotions for your CustomerIO Segments.',
      route: 'customerio-segment-tasks',
      customerio: true,
      requiresPermission: Model.permissions.Marketing.View
    }
  ]
},
{
  label: 'Inventory Adjustments',
  hideUnless: 'inventoryOperationsAreEnabled',
  requiresPermission: Model.permissions.Inventory.View,
  entries: _(ADJUSTMENT_TYPE_OPTIONS.ALL).map(adjustmentType => ({
    indent: 1,
    route: `inventory-bulk-adjustments?adjustmentType=${adjustmentType}`,
    label: `Inventory Item ${
      ADJUSTMENT_TYPES_PLURAL[adjustmentType]
    }`,
    icon: 'fal fa-tally',
    description: `${adjustmentType === ADJUSTMENT_TYPE_OPTIONS.TRANSFER_RECEIPT ? 'View and manage incoming' : 'Create and manage'} inventory item ${
      ADJUSTMENT_TYPES_PLURAL[adjustmentType].toLowerCase()
    }.`
  })).concat([{
    indent: 1,
    route: 'inventory-bulk-adjustments?uncommitted=true',
    label: 'Pending Inventory Item Adjustments',
    icon: 'fal fa-hourglass-half',
    description: 'View and manage pending inventory item adjustments.',
    notificationCountType: NOTIFICATION_COUNT_TYPES.PENDING_INVENTORY_ITEM_ADJUSTMENTS
  }]).value()
},
{
  label: 'Financial',
  circuit: true,
  requiresPermission: Model.permissions.Financial.View,
  entries: [
    {
      indent: 1,
      route: 'transfers',
      label: 'Transfers',
      description: 'View the status of transfers to your bank account.',
      icon: 'table_view'
    },
    {
      indent: 1,
      route: 'disputes',
      label: 'Disputes',
      description: 'View the status of all payment disputes.',
      icon: 'paid'
    },
    {
      indent: 1,
      route: 'journal-entry-viewer',
      label: 'Journal Entry Viewer (Beta)',
      description: 'View, approve, and edit Daily Journal Entries.',
      icon: 'account_balance'
    },
    {
      indent: 1,
      route: 'journal-entries',
      label: 'Journal Entries (Beta)',
      description: 'View tables of all Journal Entries.',
      icon: 'account_balance'
    },
    {
      indent: 1,
      route: 'accounting-days',
      label: 'Accounting Days (Beta)',
      description: 'View tables of all Accounting Days.',
      icon: 'account_balance'
    }
  ]
},
{
  label: 'Staff',
  requiresPermission: Model.permissions.Team.View,
  entries: [
    {
      indent: 1,
      route: 'teams',
      label: 'Team',
      description: 'Manage your team, add and edit members of staff.',
      icon: 'group'
    },
    {
      indent: 1,
      label: 'Time Clock Sessions',
      icon: 'list',
      description: 'View and manage Time Clock Sessions.',
      route: 'time-clock-sessions',
      site: true
    }
  ]
},
{
  label: 'Exports',
  entries: [
    {
      indent: 1,
      label: 'Marketing List',
      icon: 'insert_chart_outlined',
      description: 'Export users signed up to various marketing lists.',
      route: 'marketing-lists',
      requiresAnyPermission: [Model.permissions.Data.View, Model.permissions.Marketing.View]
    },
    {
      indent: 1,
      label: 'TMS Exports',
      icon: 'dns',
      description: 'Export TMS XML files.',
      method: 'exportTmsFiles',
      requiresPermission: Model.permissions.Data.View
    },
    {
      indent: 1,
      label: 'Polaris Exports',
      icon: 'rss_feed',
      description: 'Export Polaris Stock and Concessions files.',
      method: 'exportPolarisFiles',
      hideUnless: 'isPolarisEnabled',
      requiresPermission: Model.permissions.Data.View
    },
    {
      indent: 1,
      label: 'Export Data',
      icon: 'dns',
      description: 'Export Orders, Payments, Users, and other data.',
      route: 'exports',
      requiresPermission: Model.permissions.Data.View
    }
  ]
}]

export default {
  name: 'OperationsMenu',
  components: {
    Breadcrumbs,
    MenuItemCategory,
    MenuItemCard,
    TmsExportDialog,
    PolarisExportDialog
  },
  mixins: [AdminMenuMixin],
  configuration: ['currentSite', 'currentCircuit'],
  models: {
    pendingInventoryItemBulkAdjustmentsCount: {
      immediate: true
    }
  },
  computed: {
    pendingBulkAdjustmentsCount () {
      return this.pendingInventoryItemBulkAdjustmentsCount.value || 0
    }
  },
  beforeCreate () {
    // Required by AdminMenuMixin, before immediate watcher
    this.menuItems = operationsMenuItems
    this.basePath = 'operations'
  },
  methods: {
    notificationCount (menuItem) {
      if (menuItem.notificationCountType === NOTIFICATION_COUNT_TYPES.PENDING_INVENTORY_ITEM_ADJUSTMENTS) {
        return this.pendingBulkAdjustmentsCount
      } else {
        return null
      }
    },
    trackSelectItem (item) {
      this.$trackEvent(this.$indyUserEvents.REPORT_ITEM_SELECTED, { item })
    },
    exportTmsFiles () {
      this.$refs.tmsExportDialog.show()
    },
    isPolarisEnabled () {
      return this.currentSite.polarisEnabled
    },
    exportPolarisFiles () {
      this.$refs.polarisExportDialog.show()
    }
  },
  trackPage: true
}
</script>
